/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive } from '@angular/core';

import { AUTOMATION_GRID } from '@gh/config';
import { getClosest, isDomElement, isNotNil, logWarning } from '@gh/core-util';

import { GridApi, GridOptionsWrapper, PopupEditorWrapper, ProcessRowParams } from 'ag-grid-community';
import { isNil } from 'lodash';

import { GRID_FEATURE, GridFeature, GridFeatureInitParams, GridFeatureReadyParams } from '../feature.interfaces';
import { GridOptionsExt } from './grid-options-ext';

/**
 * Extends grid capabilities
 */
@Directive({
  selector: 'gh-grid', // tslint:disable-line:directive-selector
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridExtFeatureDirective,
    multi: true,
  }],
})
export class GridExtFeatureDirective implements GridFeature {
  // load this grid feature after all other grid features
  // this is necessary to wait until other features initialize getRowNodeId, if any
  order = Number.MAX_SAFE_INTEGER;

  process: (params: ProcessRowParams) => void;

  private gridOptionsWrapper: GridOptionsWrapper;
  private api: GridApi;
  private gridEl: Element;
  private bodyEl: Element;
  private lastPointer?: Element;

  constructor() {
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onAngularOverlayFocusOut = this.onAngularOverlayFocusOut.bind(this);
  }

  initGrid(params: GridFeatureInitParams): void {
    this.gridEl = params.nativeElement;
  }

  enhanceGridOptions(options: GridOptionsExt): void {
    if (AUTOMATION_GRID) {
      const { name, getRowNodeId, getRowNodeAutomationId, noAutomation } = options;
      if (options.hasOwnProperty('noAutomation') || options.hasOwnProperty('getRowNodeAutomationId')) {
        delete options.noAutomation;
        delete options.getRowNodeAutomationId;
      }
      if (noAutomation) {
        return;
      }
      const getAutomationId = getRowNodeAutomationId || getRowNodeId;
      if (isNil(getAutomationId)) {
        // tslint:disable-next-line:max-line-length
        logWarning('GridExtFeature: automation: grid definition does not provide implementation of' +
          ' getRowNodeId/getRowNodeAutomationId function');
        return;
      }

      this.process = (params: ProcessRowParams) => {
        const { data } = params.node;
        if (data && params.eRow) {
          const entityId = getAutomationId(data);
          if (isNil(entityId)) {
            logWarning('GridExtFeature: id is not defined (undefined or null) for entity in grid', data);
          }

          const automationId = name ? `${name}:row:${entityId}` : `row:${entityId}`;
          params.eRow.setAttribute('data-automation-id', automationId);
          params.ePinnedLeftRow.setAttribute('data-automation-id', automationId);
          params.ePinnedRightRow.setAttribute('data-automation-id', automationId);
        }
      };
    }
  }

  onGridReady(params: GridFeatureReadyParams): void {
    this.gridOptionsWrapper = params.sharedBeans.gridOptionsWrapper;
    this.api = params.api;
    this.bodyEl = this.gridEl.querySelector('.ag-body-viewport')!; // tslint:disable-line:no-non-null-assertion
    this.bodyEl.addEventListener('focusout', this.onFocusOut);
  }

  onDestroy(): void {
    this.cleanAngularOverlayFocusOut();
    this.bodyEl.removeEventListener('focusout', this.onFocusOut);
  }

  /**
   * This method completes current editing if grid loses focus.
   *
   * Implementation of this method was copied from ag-grid repository (gridPanel.ts).
   * We copied this functionality in order to support @angular/material overlay.
   * @param event
   */
  private onFocusOut(event: FocusEvent): void {
    // this is the element the focus is moving to
    const elementWithFocus = event.relatedTarget;

    // see if the element the focus is going to is part of the grid
    let clickInsideGrid = false;
    // sometimes we have to ignore click outside grid
    // (there is a problem for datepicker when user clicks on item in year/multi-year view:
    // focus moves outside datepicker content)
    let ignoreClickOutsideGrid = false;
    let isAngularOverlay: boolean = false;
    let angularOverlayElement: Maybe<HTMLElement>;
    let pointer: any = elementWithFocus;

    while (isNotNil(pointer) && !clickInsideGrid) {

      const isPopup = !!this.gridOptionsWrapper.getDomData(pointer, PopupEditorWrapper.DOM_KEY_POPUP_EDITOR_WRAPPER);
      if (isDomElement(pointer)) {
        isAngularOverlay = pointer.classList.contains('grid-overlay-panel');
        angularOverlayElement = pointer;
      }
      const isBody = this.bodyEl === pointer;

      clickInsideGrid = isPopup || isBody || isAngularOverlay;

      pointer = pointer.parentNode;
    }

    // if we click outside grid => check if previously focused element require to ignore outside clicks
    if (!clickInsideGrid) {
      // find grid overlay panel for previously focused element
      const overlayElementForTarget = getClosest(event.target, 'grid-overlay-panel');
      // check if grid overlay we moved from require to ignore outside clicks
      if (overlayElementForTarget && overlayElementForTarget.classList.contains('grid-ignore-click-outside')) {
        ignoreClickOutsideGrid = true;
        isAngularOverlay = true;
        angularOverlayElement = overlayElementForTarget;
      }
    }

    if (isAngularOverlay) {
      this.listenAngularOverlayFocusOut(angularOverlayElement!); // tslint:disable-line:no-non-null-assertion
    }

    if (!clickInsideGrid && !ignoreClickOutsideGrid) {
      this.api.stopEditing();
    }
  }

  private onAngularOverlayFocusOut(event: FocusEvent): void {
    this.cleanAngularOverlayFocusOut();
    this.onFocusOut(event);
  }

  /**
   * Listens for focusout event from given Angular overlay
   * @param pointer
   */
  private listenAngularOverlayFocusOut(pointer: Element): void {
    this.cleanAngularOverlayFocusOut();

    pointer.addEventListener('focusout', this.onAngularOverlayFocusOut);
    this.lastPointer = pointer;
  }

  /**
   * Cleans focusout listener for current angular overlay
   */
  private cleanAngularOverlayFocusOut(): void {
    if (this.lastPointer) {
      this.lastPointer.removeEventListener('focusout', this.onAngularOverlayFocusOut);
    }
  }
}
